import { Injectable } from '@angular/core';
import {
  MerchandiseService,
  PanelParams,
} from '@epicuro-next/data/merchandise';
import {
  DynamicItemsConfigEntry,
  DynamicItemsConfigWithArguments,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { RecommendationsPanels } from '@epicuro-next/models/recommendation-panels';
import { ResolveConfigService } from '@epicuro-next/platform/dynamic-components';
import { selectActiveAccountId } from '@epicuro-next/state/accounts-state';
import { selectMerchandisingDynamicPanelConfigs } from '@epicuro-next/state/home-state';
import { selectRecommendationsPanelsItemsConfig } from '@epicuro-next/state/recommendation-panels-state';
import {
  selectSiteId,
  selectSubSiteId,
} from '@epicuro-next/state/session-state';
import { select, Store } from '@ngrx/store';
import {
  combineLatest,
  map,
  mergeMap,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RecommendationPanelsProviderService {
  private readonly recommendationsPanelsItemsConfig$ = this.store.select(
    selectRecommendationsPanelsItemsConfig,
  );

  constructor(
    private readonly resolveConfigService: ResolveConfigService,
    private readonly store: Store,
    private readonly merchService: MerchandiseService,
  ) {}

  public readonly recommendationsPanelsItems$ =
    this.recommendationsPanelsItemsConfig$.pipe(
      mergeMap((recommendationsPanelsItemsConfig) => {
        return this.resolveConfigService.fetchAndMergeGroupedConfigEntries(
          recommendationsPanelsItemsConfig,
        );
      }),
      shareReplay(1),
    );

  public hasRecommendationsPanelsItems$ = this.recommendationsPanelsItems$.pipe(
    map((recommendationsPanelsItems) => {
      const record: { [key in RecommendationsPanels]: boolean } = {
        [RecommendationsPanels.AddedToBasketNotification]: false,
        [RecommendationsPanels.AddedToBasketNotificationPane]: false,
        [RecommendationsPanels.BelowCBasket]: false,
        [RecommendationsPanels.BelowConfigStrip]: false,
        [RecommendationsPanels.Checkout]: false,
        [RecommendationsPanels.Checkout2]: false,
        [RecommendationsPanels.Spotlight]: false,
        [RecommendationsPanels.PBasket]: false,
        [RecommendationsPanels.SearchBar]: false,
        [RecommendationsPanels.SearchSuggestBar]: false,
        [RecommendationsPanels.SearchProducts]: false,
        [RecommendationsPanels.ProductBasketHeader]: false,
      };

      for (const [key, value] of Object.entries(recommendationsPanelsItems)) {
        record[key as RecommendationsPanels] = value.length > 0;
      }

      return record;
    }),
  );

  public merchandiseRecommendationsPanel(slotId: string) {
    return combineLatest([
      this.store.pipe(select(selectActiveAccountId)),
      this.store.pipe(select(selectSiteId)),
      this.store.pipe(select(selectSubSiteId)),
      this.store.pipe(select(selectMerchandisingDynamicPanelConfigs)),
    ]).pipe(
      take(1),
      switchMap(([accountId, siteId, subSiteId, panelConfig]) => {
        return this.merchService
          .getPanelSlot<{ options: PanelParams[] }>(
            slotId,
            siteId,
            subSiteId,
            accountId,
          )
          .pipe(
            map((params: { options: PanelParams[] }) => ({
              params,
              panelConfig,
            })),
          );
      }),
      switchMap(({ params, panelConfig }) => {
        if (!params) {
          return of(null);
        }

        const panelConfigs = params.options.map((option) => {
          let pathPredicate: string;
          if (option.arguments) {
            const pathPredicateKey = Object.keys(option.arguments).find((key) =>
              key.includes('pathPredicate'),
            );
            if (pathPredicateKey) {
              pathPredicate = option.arguments[pathPredicateKey] as string;
            }
          }
          return this.resolveConfigService
            .resolveDynamicPanelFromPanelParams(
              option,
              panelConfig[option.panelName] as
                | DynamicItemsConfigEntry[]
                | DynamicItemsConfigWithArguments,
              undefined,
            )
            .pipe(
              map((config) => ({
                pathPredicate,
                panel: config.panel,
              })),
            );
        });
        if (panelConfigs?.length === 0) {
          return of(null);
        }
        return combineLatest(panelConfigs);
      }),
    );
  }
}
